import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAiProviders, aiSearch, aiSearchPlan, aiSearchExecute, submitAiFeedback,
  getActiveSignals, getSavedSearches, createSavedSearch,
  getActiveProductBrief,
  getPlaybooks, startPlaybook, getPlaybookRun,
  importLeads,
} from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import {
  Box, Button, Typography, Paper, Stack, Chip, TextField, MenuItem,
  CircularProgress, Alert, Checkbox, DialogActions,
  Grid, Link, LinearProgress,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
  Psychology as PsychologyIcon,
  Save as SaveIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  SelectAll as SelectAllIcon,
} from '@mui/icons-material';

export default function AiSearchPage() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [error, setError] = useState('');

  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [signals, setSignals] = useState([]);
  const [selectedSignals, setSelectedSignals] = useState([]);
  const [maxResults, setMaxResults] = useState(10);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedResults, setSelectedResults] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [activeBrief, setActiveBrief] = useState(null);
  const [saveSearchModal, setSaveSearchModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');

  const [searchPlan, setSearchPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [planSteps, setPlanSteps] = useState([]);

  const [playbooks, setPlaybooks] = useState([]);
  const [playbookModal, setPlaybookModal] = useState(false);
  const [playbookRunning, setPlaybookRunning] = useState(null);
  const [playbookResult, setPlaybookResult] = useState(null);
  const pollRef = useRef(null);

  const fetchSearchData = useCallback(async () => {
    try {
      const [provRes, sigRes, ssRes] = await Promise.all([
        getAiProviders().catch(() => ({ data: [] })),
        getActiveSignals().catch(() => ({ data: [] })),
        getSavedSearches().catch(() => ({ data: [] })),
      ]);
      setProviders(provRes.data);
      setSignals(sigRes.data);
      setSavedSearches(ssRes.data);
      const defaultProv = provRes.data.find(p => p.isDefault);
      if (defaultProv) setSelectedProvider(defaultProv.name);
    } catch {}
    try {
      const { data } = await getActiveProductBrief();
      setActiveBrief(data);
    } catch { setActiveBrief(null); }
  }, []);

  useEffect(() => { fetchSearchData(); }, [fetchSearchData]);
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleAiSearch = async () => {
    if (!searchQuery.trim()) { setError('Enter a search query'); return; }
    setError('');
    setSearching(true);
    setSearchResults(null);
    setSelectedResults([]);
    setSearchPlan(null);
    try {
      const { data } = await aiSearch({
        query: searchQuery, signalIds: selectedSignals, maxResults,
        provider: selectedProvider || undefined,
      });
      setSearchResults(data);
    } catch (err) {
      setError(err.response?.data?.message || 'AI search failed');
    } finally { setSearching(false); }
  };

  const handlePlanSearch = async () => {
    if (!searchQuery.trim()) { setError('Enter a search query'); return; }
    setError('');
    setPlanning(true);
    setSearchPlan(null);
    setSearchResults(null);
    setSelectedResults([]);
    setPlanSteps([]);
    try {
      const { data } = await aiSearchPlan({
        query: searchQuery, signalIds: selectedSignals, maxResults,
        provider: selectedProvider || undefined,
      });
      setSearchPlan(data);
      setPlanSteps(data.steps.map(s => ({ ...s, enabled: true })));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create search plan');
    } finally { setPlanning(false); }
  };

  const handleExecutePlan = async () => {
    if (!searchPlan) return;
    setError('');
    setExecuting(true);
    setSearchResults(null);
    setSelectedResults([]);
    const enabledStepIds = planSteps.filter(s => s.enabled).map(s => s.id);
    setPlanSteps(prev => prev.map(s => s.enabled ? { ...s, status: 'running' } : s));
    try {
      const { data } = await aiSearchExecute({
        plan: searchPlan, stepIds: enabledStepIds, maxResults,
        provider: selectedProvider || undefined, signalIds: selectedSignals,
      });
      setSearchResults(data);
      if (data.planSteps) {
        setPlanSteps(prev => prev.map(ps => {
          const updated = data.planSteps.find(s => s.id === ps.id);
          return updated ? { ...ps, status: updated.status, resultCount: updated.resultCount, durationSeconds: updated.durationSeconds } : ps;
        }));
      } else {
        setPlanSteps(prev => prev.map(s => s.status === 'running' ? { ...s, status: 'completed' } : s));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Plan execution failed');
      setPlanSteps(prev => prev.map(s => s.status === 'running' ? { ...s, status: 'failed' } : s));
    } finally { setExecuting(false); }
  };

  const togglePlanStep = (stepId) => {
    setPlanSteps(prev => prev.map(s => s.id === stepId ? { ...s, enabled: !s.enabled } : s));
  };

  const handleImportSelected = async () => {
    if (selectedResults.length === 0) return;
    setError('');
    try {
      const items = searchResults.results.filter((_, i) => selectedResults.includes(i));
      const leadsList = items.map(r => ({
        companyName: r.companyName, website: r.website, country: r.country,
        industryName: r.industry || undefined,
        source: 'AiSearch', sourceDetail: `${r.provider} — ${searchQuery.substring(0, 200)}`,
        notes: r.description,
      }));
      const { data } = await importLeads({ leads: leadsList });
      await dialog.alert({ title: 'Import complete', message: `Imported ${data.imported} leads (${data.duplicates} duplicates skipped)` });
      setSelectedResults([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Import failed');
    }
  };

  const handleImportAll = async () => {
    if (!searchResults?.results?.length) return;
    setError('');
    try {
      const leadsList = searchResults.results.map(r => ({
        companyName: r.companyName, website: r.website, country: r.country,
        industryName: r.industry || undefined,
        source: 'AiSearch', sourceDetail: `${r.provider} — ${searchQuery.substring(0, 200)}`,
        notes: r.description,
      }));
      const { data } = await importLeads({ leads: leadsList });
      await dialog.alert({ title: 'Import complete', message: `Imported ${data.imported} leads (${data.duplicates} duplicates skipped)` });
      setSelectedResults([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Import failed');
    }
  };

  const handleFeedback = async (idx, rating) => {
    try {
      await submitAiFeedback({
        entityType: 'AiSearchResult', entityId: idx,
        aiOperation: 'LeadSearch', rating,
        aiProviderUsed: searchResults?.provider,
      });
    } catch {}
  };

  const handleSaveSearch = async (e) => {
    e.preventDefault();
    try {
      await createSavedSearch({
        name: saveSearchName,
        criteria: JSON.stringify({ query: searchQuery, signalIds: selectedSignals }),
        keywords: searchQuery, maxResults,
      });
      setSaveSearchModal(false);
      setSaveSearchName('');
      fetchSearchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save search');
    }
  };

  const loadSavedSearch = (ss) => {
    setSearchQuery(ss.keywords || '');
    setMaxResults(ss.maxResults || 10);
    if (ss.criteria) {
      try {
        const c = JSON.parse(ss.criteria);
        if (c.signalIds) setSelectedSignals(c.signalIds);
      } catch {}
    }
  };

  const toggleSignal = (id) => {
    setSelectedSignals(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const toggleResultSelection = (idx) => {
    setSelectedResults(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  const confidenceColorRaw = (score) => {
    if (score >= 80) return '#38a169';
    if (score >= 60) return '#d69e2e';
    if (score >= 40) return '#dd6b20';
    return '#e53e3e';
  };

  const openPlaybookModal = async () => {
    try {
      const { data } = await getPlaybooks();
      setPlaybooks(data || []);
    } catch { setPlaybooks([]); }
    setPlaybookRunning(null);
    setPlaybookResult(null);
    setPlaybookModal(true);
  };

  const runPlaybook = async (playbookId) => {
    setError('');
    try {
      const { data } = await startPlaybook(playbookId);
      const runId = data.id || data.runId;
      setPlaybookRunning({ runId, steps: data.steps || [] });
      setPlaybookResult(null);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: runData } = await getPlaybookRun(runId);
          setPlaybookRunning({ runId, steps: runData.steps || [] });
          if (runData.status === 'Completed' || runData.status === 'Failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPlaybookRunning(null);
            setPlaybookResult(runData);
          }
        } catch { clearInterval(pollRef.current); pollRef.current = null; }
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start playbook');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>AI Search</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Discover new leads using AI-powered search
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<PlayArrowIcon />} onClick={openPlaybookModal}>
          Run Playbook
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 3 }}>
        {activeBrief && (
          <Alert severity="success" variant="outlined" sx={{ mb: 2.5 }}>
            <strong>Marketing Brain:</strong> {activeBrief.name} — {activeBrief.industryFocus || 'General'}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              select fullWidth size="small" label="AI Provider"
              value={selectedProvider}
              onChange={e => setSelectedProvider(e.target.value)}
            >
              {providers.map(p => (
                <MenuItem key={p.name} value={p.name}>
                  {p.displayName}{p.isDefault ? ' (default)' : ''}
                </MenuItem>
              ))}
              {providers.length === 0 && <MenuItem value="">No providers configured</MenuItem>}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              select fullWidth size="small" label="Load Saved Search"
              defaultValue=""
              onChange={e => {
                const ss = savedSearches.find(s => s.id === parseInt(e.target.value));
                if (ss) loadSavedSearch(ss);
              }}
            >
              <MenuItem value="">— Select —</MenuItem>
              {savedSearches.map(ss => <MenuItem key={ss.id} value={ss.id}>{ss.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField
              fullWidth size="small" label="Max Results" type="number"
              slotProps={{ htmlInput: { min: 1, max: 50 } }}
              value={maxResults}
              onChange={e => setMaxResults(parseInt(e.target.value) || 10)}
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth multiline rows={3} label="Search Query"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Describe the companies you're looking for... e.g., 'SaaS companies in Europe with subscription billing that have 50-500 employees'"
          sx={{ mb: 2 }}
        />

        {signals.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              Signals (click to include in search)
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {signals.map(s => (
                <Chip
                  key={s.id} label={s.text}
                  onClick={() => toggleSignal(s.id)}
                  color={selectedSignals.includes(s.id) ? 'primary' : 'default'}
                  variant={selectedSignals.includes(s.id) ? 'filled' : 'outlined'}
                  title={`Weight: ${s.weight} — ${s.category}`}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={planning ? <CircularProgress size={16} color="inherit" /> : <PsychologyIcon />}
            onClick={handlePlanSearch}
            disabled={planning || searching || executing || !searchQuery.trim()}
          >
            {planning ? 'Planning...' : 'Plan & Search'}
          </Button>
          <Button
            variant="outlined"
            startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
            onClick={handleAiSearch}
            disabled={searching || planning || executing || !searchQuery.trim()}
          >
            {searching ? 'Searching...' : 'Quick Search'}
          </Button>
          <Button
            variant="outlined" startIcon={<SaveIcon />}
            onClick={() => { setSaveSearchName(''); setSaveSearchModal(true); }}
            disabled={!searchQuery.trim()}
          >
            Save Search
          </Button>
        </Stack>

        {planning && (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>Creating search plan...</Typography>
            <Typography variant="body2">AI is analyzing your query and building a strategy</Typography>
          </Box>
        )}

        {searchPlan && !planning && (
          <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', px: 2, py: 1.5 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Search Plan</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{searchPlan.strategy}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained" size="small" onClick={handleExecutePlan}
                    disabled={executing || planSteps.filter(s => s.enabled).length === 0}
                    startIcon={executing ? <CircularProgress size={14} color="inherit" /> : null}
                  >
                    {executing ? 'Executing...' : `Execute ${planSteps.filter(s => s.enabled).length} Steps`}
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => { setSearchPlan(null); setPlanSteps([]); }}>
                    Dismiss
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {searchPlan.refinedQuery && searchPlan.refinedQuery !== searchQuery && (
              <Box sx={{ px: 2, py: 1, bgcolor: '#f0fff4', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body2">
                  <strong>Refined query:</strong> {searchPlan.refinedQuery}
                  <Button size="small" sx={{ ml: 1 }} onClick={() => setSearchQuery(searchPlan.refinedQuery)}>
                    Use this
                  </Button>
                </Typography>
              </Box>
            )}

            {searchPlan.inferredSignals?.length > 0 && (
              <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body2" component="span"><strong>Inferred signals:</strong>{' '}</Typography>
                {searchPlan.inferredSignals.map((s, i) => (
                  <Chip key={i} label={s} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.25 }} />
                ))}
              </Box>
            )}

            <Box>
              {planSteps.map((step) => (
                <Box key={step.id} sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.25,
                  borderBottom: 1, borderColor: 'grey.100',
                  bgcolor: step.status === 'completed' ? '#f0fff4'
                    : step.status === 'running' ? '#fffff0'
                    : step.status === 'failed' ? '#fff5f5'
                    : 'white',
                  opacity: step.enabled ? 1 : 0.5,
                }}>
                  <Checkbox
                    size="small" checked={step.enabled}
                    onChange={() => togglePlanStep(step.id)}
                    disabled={executing}
                    sx={{ mt: 0.25 }}
                  />
                  <Typography sx={{ fontSize: '1rem', minWidth: 20, textAlign: 'center', mt: 0.25 }}>
                    {step.status === 'completed' ? '✅'
                      : step.status === 'running' ? '🔄'
                      : step.status === 'failed' ? '❌'
                      : '⏳'}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Step {step.id}: {step.action}
                    </Typography>
                    {step.reasoning && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                        {step.reasoning}
                      </Typography>
                    )}
                    {step.status === 'completed' && (
                      <Typography variant="caption" sx={{ color: 'success.main', display: 'block', mt: 0.25 }}>
                        Found {step.resultCount} companies{step.durationSeconds ? ` · ${step.durationSeconds}s` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {searching && (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>Searching with AI...</Typography>
            <Typography variant="body2">This may take 15–30 seconds</Typography>
          </Box>
        )}

        {searchResults && !searching && (
          <Box>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body1">
                <strong>{searchResults.totalFound}</strong> results from{' '}
                <strong>{searchResults.provider}</strong> ({searchResults.model})
                {searchResults.tokensUsed > 0 && (
                  <Typography component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                    ({searchResults.tokensUsed} tokens)
                  </Typography>
                )}
              </Typography>
              <Stack direction="row" spacing={1}>
                {selectedResults.length > 0 && (
                  <Button variant="contained" size="small" onClick={handleImportSelected}>
                    Import {selectedResults.length} Selected
                  </Button>
                )}
                {searchResults.results.length > 0 && (
                  <>
                    <Button
                      variant="outlined" size="small" startIcon={<SelectAllIcon />}
                      onClick={() => setSelectedResults(prev =>
                        prev.length === searchResults.results.length
                          ? []
                          : searchResults.results.map((_, i) => i)
                      )}
                    >
                      {selectedResults.length === searchResults.results.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button variant="contained" size="small" onClick={handleImportAll}>
                      Import All
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>

            {searchResults.results.length === 0 ? (
              <Paper sx={{ p: 5, textAlign: 'center' }}>
                <Typography sx={{ color: 'text.secondary' }}>
                  No results found. Try broadening your search.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {searchResults.results.map((r, idx) => (
                  <Paper
                    key={idx} variant="outlined"
                    sx={{
                      p: 2,
                      borderColor: selectedResults.includes(idx) ? 'primary.main' : 'divider',
                      bgcolor: selectedResults.includes(idx) ? 'primary.50' : 'background.paper',
                    }}
                  >
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flex: 1 }}>
                        <Checkbox
                          size="small"
                          checked={selectedResults.includes(idx)}
                          onChange={() => toggleResultSelection(idx)}
                        />
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>{r.companyName}</Typography>
                          {r.website && (
                            <Link
                              href={r.website.startsWith('http') ? r.website : `https://${r.website}`}
                              target="_blank" rel="noreferrer"
                              sx={{ fontSize: '0.85rem' }}
                            >
                              {r.website}
                            </Link>
                          )}
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: confidenceColorRaw(r.confidenceScore) }}>
                          {r.confidenceScore}%
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            size="small" variant="outlined"
                            onClick={() => handleFeedback(idx, 'Useful')}
                            title="Useful" sx={{ minWidth: 36, p: 0.5 }}
                          >
                            <ThumbUpIcon fontSize="small" />
                          </Button>
                          <Button
                            size="small" variant="outlined"
                            onClick={() => handleFeedback(idx, 'NotRelevant')}
                            title="Not Relevant" sx={{ minWidth: 36, p: 0.5 }}
                          >
                            <ThumbDownIcon fontSize="small" />
                          </Button>
                        </Stack>
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ my: 1, flexWrap: 'wrap' }}>
                      {r.industry && <Chip label={r.industry} size="small" variant="outlined" />}
                      {r.country && <Chip label={r.country} size="small" variant="outlined" />}
                      <Chip label={r.source} size="small" variant="outlined" />
                    </Stack>

                    {r.description && (
                      <Typography variant="body2" sx={{ my: 1, color: 'text.secondary' }}>{r.description}</Typography>
                    )}

                    {r.matchReasons?.length > 0 && (
                      <Box sx={{ my: 1 }}>
                        {r.matchReasons.map((reason, i) => (
                          <Typography key={i} variant="body2" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckIcon sx={{ fontSize: 16 }} /> {reason}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {r.disqualifierWarnings?.length > 0 && (
                      <Box sx={{ my: 1 }}>
                        {r.disqualifierWarnings.map((warn, i) => (
                          <Typography key={i} variant="body2" sx={{ color: '#dd6b20', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <WarningIcon sx={{ fontSize: 16 }} /> {warn}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Paper>

      {/* Save Search Modal */}
      {saveSearchModal && (
        <Modal title="Save Search" onClose={() => setSaveSearchModal(false)}>
          <form onSubmit={handleSaveSearch}>
            <TextField
              fullWidth required label="Search Name"
              value={saveSearchName}
              onChange={e => setSaveSearchName(e.target.value)}
              placeholder="e.g., Nordic SaaS with billing pain"
              sx={{ mb: 2 }} size="small"
            />
            <DialogActions sx={{ px: 0, pb: 0 }}>
              <Button variant="outlined" onClick={() => setSaveSearchModal(false)}>Cancel</Button>
              <Button variant="contained" type="submit">Save</Button>
            </DialogActions>
          </form>
        </Modal>
      )}

      {/* Playbook Modal */}
      {playbookModal && (
        <Modal title="Run Playbook" onClose={() => { if (pollRef.current) clearInterval(pollRef.current); setPlaybookModal(false); }}>
          {!playbookRunning && !playbookResult && (
            <Box>
              {playbooks.length === 0 ? (
                <Typography sx={{ color: 'text.secondary' }}>
                  No playbooks available. Create one in Management first.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {playbooks.map(pb => (
                    <Paper
                      key={pb.id} variant="outlined"
                      sx={{ p: 2, cursor: 'pointer', transition: 'border-color 0.2s', '&:hover': { borderColor: 'primary.main' } }}
                      onClick={() => runPlaybook(pb.id)}
                    >
                      <Typography sx={{ fontWeight: 600 }}>{pb.name}</Typography>
                      {pb.description && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{pb.description}</Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
                        {pb.steps?.length || 0} steps · Click to run
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {playbookRunning && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>Running...</Typography>
              <LinearProgress sx={{ mb: 2 }} />
              <Stack spacing={1}>
                {(playbookRunning.steps || []).map((step, idx) => {
                  const statusIcon = step.status === 'Completed' ? '✓' : step.status === 'Running' ? '⟳' : step.status === 'Failed' ? '✗' : '○';
                  const statusColor = step.status === 'Completed' ? 'success.main' : step.status === 'Running' ? 'primary.main' : step.status === 'Failed' ? 'error.main' : 'text.disabled';
                  return (
                    <Box key={idx} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
                      borderRadius: 1.5, bgcolor: step.status === 'Running' ? 'primary.50' : 'transparent',
                    }}>
                      <Typography sx={{ fontSize: '1.2rem', color: statusColor, fontWeight: 700, width: 24, textAlign: 'center' }}>
                        {statusIcon}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {step.name || step.type || `Step ${idx + 1}`}
                        </Typography>
                        {step.resultCount != null && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{step.resultCount} results</Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: statusColor, fontWeight: 600 }}>{step.status}</Typography>
                    </Box>
                  );
                })}
              </Stack>
              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary', textAlign: 'center' }}>
                Auto-refreshing every 5 seconds...
              </Typography>
            </Box>
          )}

          {playbookResult && (
            <Box>
              <Alert severity={playbookResult.status === 'Completed' ? 'success' : 'error'} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {playbookResult.status === 'Completed' ? 'Playbook Complete!' : 'Playbook Failed'}
                </Typography>
              </Alert>
              {playbookResult.steps && (
                <Box sx={{ mb: 2 }}>
                  {playbookResult.steps.map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="body2">{s.name || s.type || `Step ${i + 1}`}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {s.resultCount != null ? `${s.resultCount} results` : s.status}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <DialogActions sx={{ px: 0, pb: 0 }}>
                <Button variant="outlined" onClick={() => setPlaybookModal(false)}>Close</Button>
                <Button variant="contained" onClick={() => { setPlaybookModal(false); navigate('/leads'); }}>
                  View Leads
                </Button>
              </DialogActions>
            </Box>
          )}
        </Modal>
      )}
    </Box>
  );
}
